"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PdaVerifier = exports.PdaMapper = void 0;
const logger_1 = require("../interfaces/logger");
const verifierLogger = new logger_1.VoidLogger();
class PdaMapper {
    /**
     * Maps a raw array to a structured object based on a schema.
     * Guaranteed to preserve all data and track mapped indices.
     */
    static map(raw, schema) {
        if (!Array.isArray(raw)) {
            // Some API responses might return something else on error, handle it gracefully
            return { _raw: [raw] };
        }
        const result = {
            _raw: raw,
            _unknown: {}
        };
        const mappedIndices = new Set();
        for (const [key, value] of Object.entries(schema)) {
            if (value === -1)
                continue;
            if (typeof value === 'number') {
                result[key] = raw[value];
                mappedIndices.add(value);
            }
            else {
                const mapping = value;
                const indices = Array.isArray(mapping.index) ? mapping.index : [mapping.index];
                // Track indices
                indices.forEach(i => mappedIndices.add(i));
                // Get source data (either single value or slice)
                const source = Array.isArray(mapping.index)
                    ? mapping.index.map(i => raw[i])
                    : raw[mapping.index];
                if (mapping.ignore) {
                    continue;
                }
                if (mapping.map) {
                    result[key] = mapping.map(source, raw);
                }
                else if (mapping.schema) {
                    result[key] = this.map(source, mapping.schema);
                }
                else if (mapping.list) {
                    result[key] = this.mapList(source, mapping.list);
                }
                else {
                    result[key] = source;
                }
            }
        }
        // Collect unknown fields
        for (let i = 0; i < raw.length; i++) {
            if (!mappedIndices.has(i)) {
                result._unknown[i] = raw[i];
            }
        }
        // Clean up empty unknown object
        if (Object.keys(result._unknown).length === 0) {
            delete result._unknown;
        }
        return result;
    }
    static mapList(rawList, schema) {
        if (!Array.isArray(rawList))
            return [];
        return rawList.map(item => this.map(item, schema));
    }
    /**
     * Safely converts a unix timestamp to an ISO string.
     * Returns null if the timestamp is 0, missing, or invalid.
     */
    static safeIsoDate(ts) {
        if (ts === null || ts === undefined || ts === 0 || ts === "0" || ts === "")
            return null;
        try {
            const numTs = Number(ts);
            if (isNaN(numTs))
                return null;
            const date = new Date(numTs * 1000);
            return isNaN(date.getTime()) ? null : date.toISOString();
        }
        catch {
            return null;
        }
    }
    /**
     * Decodes basic HTML entities and numeric entities.
     */
    static decodeNumericEntity(dec) {
        const n = Number(dec);
        if (!Number.isFinite(n) || n < 0 || n > 0x10ffff)
            return '';
        if (n > 0xffff) {
            const adjusted = n - 0x10000;
            return String.fromCharCode(0xd800 + (adjusted >> 10), 0xdc00 + (adjusted & 0x3ff));
        }
        return String.fromCharCode(n);
    }
    /** Декод HTML-сущностей без trim (для `[code]` и точного сохранения пробелов). */
    static decodeEntities(val) {
        return val
            .replace(/&#(\d+);/g, (_, dec) => PdaMapper.decodeNumericEntity(dec))
            .replace(/&#x([0-9a-fA-F]+);/g, (_, hex) => PdaMapper.decodeNumericEntity(String(parseInt(hex, 16))))
            .replace(/&quot;/g, '"')
            .replace(/&amp;/g, '&')
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&nbsp;/g, ' ')
            .replace(/&rsquo;/g, "'")
            .replace(/&lsquo;/g, "'")
            .replace(/&ldquo;/g, '"')
            .replace(/&rdquo;/g, '"');
    }
    static cleanString(val) {
        if (typeof val !== 'string')
            return val;
        return PdaMapper.decodeEntities(val).trim();
    }
}
exports.PdaMapper = PdaMapper;
/**
 * Verifier to find unmapped data in API responses.
 * Helps maintaining 1:1 mapping with the 4PDA API.
 */
class PdaVerifier {
    static verify(obj, context = 'General') {
        if (!obj || typeof obj !== 'object')
            return;
        if (obj._unknown && Object.keys(obj._unknown).length > 0) {
            verifierLogger.warn(`Unmapped indices in ${context}:`, obj._unknown);
        }
        // Recursively check nested objects
        for (const [key, value] of Object.entries(obj)) {
            if (key === '_raw' || key === '_unknown')
                continue;
            if (Array.isArray(value)) {
                value.forEach((item, i) => this.verify(item, `${context}.${key}[${i}]`));
            }
            else if (value && typeof value === 'object') {
                this.verify(value, `${context}.${key}`);
            }
        }
    }
    /**
     * Strict verification: throws if anything is unmapped.
     * Use this in tests.
     */
    static assertMapped(obj, context = 'General') {
        if (obj?._unknown && Object.keys(obj._unknown).length > 0) {
            throw new Error(`[PdaVerifier] STRICT FAILURE: Unmapped indices in ${context}: ${JSON.stringify(obj._unknown)}`);
        }
    }
}
exports.PdaVerifier = PdaVerifier;
